import { Component, OnInit } from '@angular/core';
import { Translatable } from '@app/translate/models/translatable';
import { BootstrapSemanticVariation } from '@app/utils/bootstrap';
import { PortalError } from '@app/utils/error/portal-error';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { from, isObservable, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DialogComponent } from '../dialog-component.directive';
import { ConfirmationDialogFunction, ConfirmationDialogInput } from './confirmation-dialog-input';

@UntilDestroy()
@Component({
  selector: 'portal-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  standalone: false,
})
export class ConfirmationDialogComponent
  extends DialogComponent<ConfirmationDialogInput, void>
  implements OnInit, ConfirmationDialogInput
{
  cancel?: ConfirmationDialogFunction;
  cancelText: Translatable;
  ok: ConfirmationDialogFunction;
  okText: Translatable;
  title: Translatable;
  text: Translatable;
  variant: BootstrapSemanticVariation = 'primary';

  // Instance parameters
  error: PortalError;
  errors: PortalError[];
  loading: boolean = false;

  constructor(ngbActiveModal: NgbActiveModal) {
    super(ngbActiveModal);
  }

  ngOnInit(): void {}

  onDismissed(reason?: any): void {
    this.trigger(false);
  }

  trigger(value: boolean) {
    const f = value ? this.ok : this.cancel;

    if (typeof f === 'function') {
      let result: boolean | void | PromiseLike<PortalError | void> | Observable<PortalError | void> = f();

      if (result instanceof Promise) {
        // Wrap the promise in an observable, so we can use the same way to handle both cases
        result = from(result);
      }

      if (isObservable(result)) {
        this.error = undefined;
        this.errors = [];
        this.loading = true;

        // Subscribe to the observable
        result
          .pipe(
            untilDestroyed(this),
            catchError((error) =>
              of({
                error,
                type: 'error',
              })
            )
          )
          .subscribe((result: CaughtError | boolean | any) => {
            this.loading = false;

            if (typeof result === 'boolean') {
              if (result === true) {
                this.close();
              }
            } else if (result?.type === 'error') {
              this.loading = false;

              if (Array.isArray(result.error)) {
                this.errors = result.error;
              } else {
                this.error = result.error;
              }
            } else {
              // Backwards compatibility
              this.loading = false;
              this.close();
            }
          });
      } else if (typeof result === 'boolean') {
        // Hide if the returned boolean indicates success
        if (result === true) {
          this.close();
        }
      } else {
        // Backwards compatibility
        this.close();
      }
    } else {
      this.dismiss();
    }
  }
}

interface CaughtError {
  error: any;
  type: 'error';
}
